import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from '@amzn/awsui-components-console/icon';
import { useDarkMode } from 'src/common/ui/DarkModeContext';
import { getUsernameFromJwt } from 'src/common/utils/auth';
import {
  SUPPORTED_LANGUAGES,
  BADGE_PHOTO_URL,
  STRIDER_WIKI_URL,
  FEEDBACK_URL,
} from 'src/common/constants';
import swooshUrl from 'src/images/langhub-swoosh.svg';
import langhubLogo from 'src/images/langhub-logo.png';
import styles from './AppHeader.module.scss';

export interface AppHeaderProps {
  /** Optional content to render between the logo and the utilities (nav links, breadcrumbs, etc.) */
  navContent?: React.ReactNode;
  /** When true, hides the dark mode toggle button from the utility bar */
  hideDarkModeToggle?: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  navContent,
  hideDarkModeToggle,
}) => {
  const username = getUsernameFromJwt();
  const [imgFailed, setImgFailed] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [locale, setLocale] = useState('en-US');
  const navigate = useNavigate();

  /**
   * Route to /home in-app instead of letting the browser follow the href, which
   * would reload the whole document (re-downloading the bundle, discarding the
   * Apollo cache, and re-running the Midway session check). The href is kept so
   * the logo stays a real link for accessibility, and modified clicks
   * (new tab/window, middle click) fall through to the browser unchanged.
   */
  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate('/home');
  };

  return (
    <header className={`${styles.bar} langhub-app-header`}>
      {/* Left cluster: identity + optional nav content */}
      <div className={styles.left}>
        <a
          href="/home"
          className={styles.identity}
          aria-label="LangHub home"
          onClick={handleLogoClick}
        >
          <img src={langhubLogo} height={25} alt="" />
          <span className={styles.wordmark}>
            <span className={styles.title}>LangHub</span>
            <img
              src={swooshUrl}
              alt=""
              aria-hidden="true"
              width={80}
              height={15}
              className={styles.swoosh}
            />
          </span>
        </a>
        {navContent}
      </div>

      {/* Right cluster: utilities */}
      <div className={styles.utilities}>
        {!hideDarkModeToggle && (
          <button
            type="button"
            className={styles.utilSm}
            aria-label={
              isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
            }
            onClick={toggleDarkMode}
            data-testid="dark-mode-toggle"
          >
            <Icon name="light-dark" />
          </button>
        )}

        <a
          className={styles.utilSm}
          href={STRIDER_WIKI_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Help"
        >
          Help
        </a>

        <a
          className={styles.utilSm}
          href={FEEDBACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Give Feedback"
        >
          Feedback
        </a>

        <select
          className={styles.langSelect}
          aria-label="Language"
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
        >
          {SUPPORTED_LANGUAGES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={`${styles.utilSm} ${styles.userBtn}`}
          aria-label="User menu"
        >
          {username && !imgFailed ? (
            <img
              src={`${BADGE_PHOTO_URL}${username}`}
              alt=""
              aria-hidden="true"
              className={styles.avatar}
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className={styles.avatarFallback} aria-hidden="true">
              {username ? username.charAt(0).toUpperCase() : ''}
            </span>
          )}
        </button>
      </div>
    </header>
  );
};

export default AppHeader;
